import { Feature } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { FOOTER, NUMBER, DATE } from '@src/slide/domain/ui-model/constant/place-holder';
import { transaction } from '@src/slide/domain/slide-app';
import React from 'react';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import Checkbox from '@tencent/dui/lib/components/Checkbox';
import Input from '@tencent/dui/lib/components/Input';
import i18n from '@i18next/i18n';
import { FormItemLabelName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import Style from '@framework/workbench/drawer/components/dropdown/index.less';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { IShapeBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { DatetimeFieldType } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/format-datetime';
import { DatetimeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-footer/slide-footer.interface';
import { IMasterDataModel } from '@tencent/slide-sdk-model/src/model/master-data-model';
import { IPresentationDataModel } from '@tencent/slide-sdk-model';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

@Feature()
export class SlideFooterFeature extends BaseDrawerFeature {
    // 对于custom的input元素，标志是否不使用defalutValue而使用value的字段
    // dui的input组件的defalutValue只有在初始化时生效一次，后续不再生效，导致切页时由于复用了组件而导致输入框不刷新
    private useValue = false;
    public getSlideNumberContent() {
        const checked = this.hasSlideNumberPlaceHolder();
        const disabled = this.shouldSlideNumberDisabled();
        const tooltipText = disabled
            ? <div>{i18n.t('当前版式未设置页脚，请前往母版中新增。')}
                <span onClick={this.handleSetMaster} className={Style['dropdown-tooltip-link']}>{i18n.t('去设置')}</span>
            </div> : undefined;
        return (<Tooltip
            title={tooltipText}
            disabled={!tooltipText}
            className={Style['dropdown-tooltip']}
        >
            <Checkbox
                style={{ fontSize: '12px', margin: '4px 0' }}
                checked={checked}
                onChange={this.handleNumberCheckStateChange}
                disabled={disabled}
            >{FormItemLabelName.SLIDE_NUMBER}</Checkbox>
        </Tooltip>);
    }

    public getSlideFooterContent() {
        const checked = this.hasSlideFooterPlaceHolder();
        const disabled = this.shouldSlideFooterDisabled();
        const tooltipText = disabled
            ? <div>{i18n.t('当前版式未设置页脚，请前往母版中新增。')}
                <span onClick={this.handleSetMaster} className={Style['dropdown-tooltip-link']}>{i18n.t('去设置')}</span>
            </div> : undefined;
        return (<Tooltip
            title={tooltipText}
            disabled={!tooltipText}
            className={Style['dropdown-tooltip']}
        >
            <Checkbox
                style={{ fontSize: '12px', margin: '4px 0' }}
                checked={checked}
                onChange={this.handleFooterCheckStateChange}
                disabled={disabled}
            >{FormItemLabelName.SLIDE_FOOTER}</Checkbox>
        </Tooltip>);
    }

    public getSlideFooterInputContent() {
        const { currentSlide } = this.getSelectionService();
        const disabled = this.shouldSlideFooterInputDisabled();
        const displayValue = currentSlide?.slideFooter.getCurrentSlideFooterContent();
        let value;
        if (this.useValue) {
            value = displayValue;
        }
        return <Input
            disabled={disabled}
            defaultValue={displayValue}
            value={value}
            onChange={this.updateSlideFooterContent.bind(this)}
            style={{ fontSize: '12px' }}
            inputStyle={{ height: '28px' }}
        />;
    }

    public getDatetimeContent() {
        const checked = this.hasDatetimePlaceHolder();
        const disabled = this.shouldDatetimeDisabled();
        const tooltipText = disabled
            ? <div>{i18n.t('当前版式未设置页脚，请前往母版中新增。')}
                <span onClick={this.handleSetMaster} className={Style['dropdown-tooltip-link']}>{i18n.t('去设置')}</span>
            </div> : undefined;
        return (<Tooltip
            title={tooltipText}
            disabled={!tooltipText}
            className={Style['dropdown-tooltip']}
        >
            <Checkbox
                style={{ fontSize: '12px', margin: '4px 0' }}
                checked={checked}
                onChange={this.handleDatetimeCheckStateChange}
                disabled={disabled}
            >{FormItemLabelName.DATETIME}</Checkbox>
        </Tooltip>);
    }

    public getDatetimeInputContent() {
        const { currentSlide } = this.getSelectionService();
        const disabled = this.shouldDatetimeOthersDisabled();
        const displayValue = currentSlide?.slideFooter.getCurrentDatetimeText();
        let value;
        if (this.useValue) {
            value = displayValue;
        }
        return <Input
            disabled={disabled}
            value={value}
            defaultValue={displayValue}
            onChange={this.updateDatetimeContent.bind(this)}
            style={{ fontSize: '12px' }}
            inputStyle={{ height: '28px' }}
        />;
    }

    public getSlideNumberStartIndex(): number | null {
        // -1为用户编辑过程中清空文本框的场景，此时文本框通过sdk原始数据判定显示为空，而页面其他地方显示为格式化后的ui firstSlideNum
        const sdkFirstSlideNum = (this.getUiDataService().presentation.sdkData as IPresentationDataModel)
            .props.firstSlideNum;
        return sdkFirstSlideNum === -1 ? null : this.getUiDataService().presentation.firstSlideNum;
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.SLIDE_FOOTER)
    public updateSlideNumberStartIndex(index: number): void {
        if (this.isInputOnlyChangeUI()) return;
        const maxIndex = 9999;
        const minIndex = 0;
        const updateElement = this.getUiDataService().presentation.sdkData;
        if (this.getSlideNumberStartIndex() === index) {
            return;
        }
        // 3.0无法将null存入，所以靠-1来标识当前是清空文本框状态
        const firstSlideNum = index !== null ? Math.max(minIndex, Math.min(index, maxIndex)) : -1;
        (updateElement as IPresentationDataModel).api.updateProps({ firstSlideNum });
    }

    public shouldSlideNumberStartDisabled(): boolean {
        return !this.hasSlideNumberPlaceHolder();
    }

    public updateDatetimeType(value: DatetimeType) {
        this.getSelectionService()?.currentSlide?.slideFooter.updateDatetimeType(value);
    }

    public getDatetimeType(): DatetimeType {
        const { currentSlide } = this.getSelectionService();
        if (!currentSlide) {
            return DatetimeType.AUTO_UPDATE;
        }
        return currentSlide.slideFooter.getDatetimeType();
    }

    public getDatetimeFieldType() {
        return this.getSelectionService().currentSlide?.slideFooter.getDatetimeFieldType();
    }

    public updateInputStateOnChangePage() {
        this.useValue = true;
    }

    public updateDatetimeFieldType(type: DatetimeFieldType) {
        this.getSelectionService().currentSlide?.slideFooter.updateDatetimeFieldType(type);
    }

    /**
     * 将当前页的页脚设置同步到所有可用的幻灯片
     */
    public handleApplyButtonClick(): void {
        const { currentSlide } = this.getSelectionService();
        const slideNumberCheckState = this.hasSlideNumberPlaceHolder();
        const footerCheckState = this.hasSlideFooterPlaceHolder();
        const datetimeCheckState = this.hasDatetimePlaceHolder();
        const footerContent = currentSlide?.slideFooter.getCurrentSlideFooterContent();
        const datetimeParagraphList = currentSlide?.slideFooter.getDatetimeParagraphList();
        const footerParagraphList = [{
            textRuns: [{
                text: footerContent,
            }],
        }];
        transaction(() => {
            const masterModel = currentSlide?.getRelationMasterModel?.() || {};
            (masterModel.sdkData as IMasterDataModel).api.updateHeaderFooter({
                sldNum: slideNumberCheckState,
                ftr: footerCheckState,
                dt: datetimeCheckState,
            });
            // 更新master上的占位符内容
            masterModel.children?.forEach((child) => {
                // 页脚
                if (child.isFooterPl) {
                    child.updateParagraphList(footerParagraphList, {});
                }
                // 日期
                if (child.isDatePl) {
                    child.updateParagraphList(datetimeParagraphList, {});
                }
            });
            // 更新版式上的占位符内容
            masterModel.layoutList?.forEach((layoutModel) => {
                layoutModel?.children.forEach((child) => {
                    // 页脚
                    if (child.isFooterPl) {
                        child.updateParagraphList(footerParagraphList, {});
                    }
                    // 日期
                    if (child.isDatePl) {
                        child.updateParagraphList(datetimeParagraphList, {});
                    }
                });
            });
            // 更新每页slide上的选中状态
            this.getUiDataService().slideModelList.forEach((slideModel) => {
                slideModel?.children.forEach((child: IShapeBaseModel) => {
                    // 页脚
                    if (child.isFooterPl) {
                        child.updateParagraphList(footerParagraphList, {});
                    }
                    // 日期
                    if (child.isDatePl) {
                        child.updateParagraphList(datetimeParagraphList, {});
                    }
                });
                slideModel.updatePlaceholderFormat({
                    checked: slideNumberCheckState,
                    value: NUMBER,
                });
                slideModel.updatePlaceholderFormat({
                    checked: footerCheckState,
                    value: FOOTER,
                });
                slideModel.updatePlaceholderFormat({
                    checked: datetimeCheckState,
                    value: DATE,
                });
            });
        });

        this.getSelectionService().selectedElements.clear();
    }

    public shouldDatetimeOthersDisabled(): boolean {
        return !this.hasDatetimePlaceHolder();
    }

    private updateSlideFooterContent(value: string): void {
        this.useValue = false;
        this.getSelectionService().currentSlide?.slideFooter.updateSlideFooterContent(value);
    }

    private updateDatetimeContent(value: string): void {
        this.useValue = false;
        const { currentSlide } = this.getSelectionService() || {};
        currentSlide?.slideFooter.updateDatetimeContent(value);
    }

    private shouldSlideFooterInputDisabled(): boolean {
        return !this.hasSlideFooterPlaceHolder();
    }

    private shouldSlideNumberDisabled(): boolean {
        const { currentSlide } = this.getSelectionService();
        const { childrenPlaceholderInfo: layoutPlaceholderInfo } = currentSlide?.getRelationLayoutModel?.() || {};
        // 当前页和当前板式没有页码占位，页码选择框置灰
        return !(this.hasSlideNumberPlaceHolder() || layoutPlaceholderInfo?.hasSlideNum);
    }

    private shouldSlideFooterDisabled(): boolean {
        const { currentSlide } = this.getSelectionService();
        const { childrenPlaceholderInfo } = currentSlide?.getRelationLayoutModel?.() || {};
        // 当前页和当前板式没有页脚占位，页脚选择框置灰
        return !(this.hasSlideFooterPlaceHolder() || childrenPlaceholderInfo?.hasFooter);
    }

    private shouldDatetimeDisabled(): boolean {
        const { currentSlide } = this.getSelectionService();
        const { childrenPlaceholderInfo } = currentSlide?.getRelationLayoutModel?.() || {};
        // 当前页和当前板式没有页脚占位，页脚选择框置灰
        return !(this.hasDatetimePlaceHolder() || childrenPlaceholderInfo?.hasDate);
    }

    private handleNumberCheckStateChange = (checked: boolean): void => {
        this.updateProps(ActionType.PLACEHOLDER_FORMAT, { checked, value: NUMBER });
    };

    private handleFooterCheckStateChange = (checked: boolean): void => {
        this.useValue = true;
        this.updateProps(ActionType.PLACEHOLDER_FORMAT, { checked, value: FOOTER });
    };

    private handleDatetimeCheckStateChange = (checked: boolean): void => {
        this.updateProps(ActionType.PLACEHOLDER_FORMAT, { checked, value: DATE });
    };

    /**
     * 当前页是否存在页码占位
     */
    private hasSlideNumberPlaceHolder(): boolean {
        const { childrenPlaceholderInfo } = this.getSelectionService().currentSlide || {};
        return !!childrenPlaceholderInfo?.hasSlideNum;
    }

    /**
     * 当前页是否存在页脚占位
     */
    private hasSlideFooterPlaceHolder(): boolean {
        const { childrenPlaceholderInfo } = this.getSelectionService().currentSlide || {};
        return !!childrenPlaceholderInfo?.hasFooter;
    }

    /**
     * 当前页是否存在日期占位
     */
    private hasDatetimePlaceHolder(): boolean {
        const { childrenPlaceholderInfo } = this.getSelectionService().currentSlide || {};
        return !!childrenPlaceholderInfo?.hasDate;
    }

    private handleSetMaster = () => {
        // 进入母版编辑自动跳转到对应板式页
        const layoutId = this.getSelectionService().currentSlide?.getRelationLayoutModel().id;
        this.getSelectionService().lastMasterEditPageIndex = this.getUiDataService()
            .layoutModelList.findIndex(i => i.id === layoutId);
        this.getSelectionService().lastMasterEditSlideType = UIModelType.LAYOUT;
        this.getMenuManager()?.toolbarMenu()
            ?.callback(MenuType.TOOLBAR, ActionType.MASTER_LAYOUT);
    };
}
